import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface Appointment {
  id: string;
  quote_draft_id: string | null;
  ghl_appointment_id: string;
  ghl_calendar_id: string;
  contact_id: string | null;
  scheduled_at: string;
  duration_minutes: number;
  status: string;
  is_draft_appointment: boolean;
  deal_closed: boolean;
  deal_closed_at: string | null;
  payment_status: string;
  appointment_data: any;
  cancellation_reason: string | null;
  created_at: string;
  updated_at: string;
  sales_rep_id: string | null;
  assigned_to_sales_rep_id: string | null;
  ghl_contacts?: {
    id: string;
    email: string;
    phone: string | null;
    first_name: string | null;
    last_name: string | null;
    company_name: string | null;
  } | null;
}

export interface AppointmentFilters {
  dateRange?: { start: Date; end: Date };
  searchTerm?: string;
  salesRepId?: string;
}

export const useAppointments = (
  salesRepId?: string | null,
  filters?: AppointmentFilters
) => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('appointments')
        .select(`
          *,
          ghl_contacts!fk_appointments_contact (
            id,
            email,
            phone,
            first_name,
            last_name,
            company_name
          )
        `)
        .is('archived_at', null) // Exclude archived appointments by default
        .order('scheduled_at', { ascending: false });

      // Note: We don't filter by salesRepId here because:
      // 1. RLS policies handle access control
      // 2. Appointments are linked via appointment_data.assignedUserId (GHL user ID) matching profiles.ghl_user_id
      // 3. We'll filter client-side to check both direct assignments and GHL assignedUserId

      // Apply date range filter
      if (filters?.dateRange) {
        query = query
          .gte('scheduled_at', filters.dateRange.start.toISOString())
          .lte('scheduled_at', filters.dateRange.end.toISOString());
      }

      // Apply sales rep filter (for admins viewing specific rep)
      if (filters?.salesRepId && filters.salesRepId !== 'all') {
        query = query.or(
          `sales_rep_id.eq.${filters.salesRepId},assigned_to_sales_rep_id.eq.${filters.salesRepId}`
        );
      }

      const { data, error } = await query;

      if (error) throw error;

      // Apply sales rep filter client-side
      // Appointments are linked via appointment_data.assignedUserId (GHL user ID) matching profiles.ghl_user_id
      let filteredData = data || [];
      if (salesRepId && filteredData.length > 0) {
        // First, fetch the user's ghl_user_id
        const { data: profile } = await supabase
          .from('profiles')
          .select('ghl_user_id')
          .eq('id', salesRepId)
          .maybeSingle();

        const userGhlUserId = profile?.ghl_user_id;

        filteredData = filteredData.filter((apt) => {
          // Check direct assignment
          if (apt.sales_rep_id === salesRepId || apt.assigned_to_sales_rep_id === salesRepId) {
            return true;
          }
          // Check via GHL assignedUserId (stored in appointment_data)
          if (userGhlUserId && apt.appointment_data?.assignedUserId === userGhlUserId) {
            return true;
          }
          return false;
        });
      }

      // Enrich appointments with sales rep profiles
      // Since sales_rep_id references auth.users, we need to look up via profiles table
      if (filteredData.length > 0) {
        const salesRepIds = new Set<string>();
        const ghlUserIds = new Set<string>();
        
        filteredData.forEach((apt) => {
          if (apt.sales_rep_id) salesRepIds.add(apt.sales_rep_id);
          if (apt.assigned_to_sales_rep_id) salesRepIds.add(apt.assigned_to_sales_rep_id);
          if (apt.appointment_data?.assignedUserId) ghlUserIds.add(apt.appointment_data.assignedUserId);
        });

        // Fetch profiles by ID and by ghl_user_id
        const profileQueries = [];
        if (salesRepIds.size > 0) {
          profileQueries.push(
            supabase
              .from('profiles')
              .select('id, email, full_name, ghl_user_id')
              .in('id', Array.from(salesRepIds))
          );
        }
        if (ghlUserIds.size > 0) {
          profileQueries.push(
            supabase
              .from('profiles')
              .select('id, email, full_name, ghl_user_id')
              .in('ghl_user_id', Array.from(ghlUserIds))
          );
        }

        const profileResults = await Promise.all(profileQueries);
        const profilesMap = new Map<string, any>();
        const profilesByGhlId = new Map<string, any>();

        profileResults.forEach((result) => {
          if (result.data) {
            result.data.forEach((profile: any) => {
              profilesMap.set(profile.id, profile);
              if (profile.ghl_user_id) {
                profilesByGhlId.set(profile.ghl_user_id, profile);
              }
            });
          }
        });

        // Enrich appointments with sales rep data
        filteredData = filteredData.map((apt) => {
          const salesRep = apt.sales_rep_id ? profilesMap.get(apt.sales_rep_id) : null;
          const assignedRep = apt.assigned_to_sales_rep_id ? profilesMap.get(apt.assigned_to_sales_rep_id) : null;
          const ghlAssignedRep = apt.appointment_data?.assignedUserId 
            ? profilesByGhlId.get(apt.appointment_data.assignedUserId) 
            : null;

          return {
            ...apt,
            sales_rep: salesRep || ghlAssignedRep,
            assigned_rep: assignedRep || ghlAssignedRep,
          };
        });
      }
      if (filters?.searchTerm && filteredData.length > 0) {
        const searchLower = filters.searchTerm.toLowerCase();
        filteredData = filteredData.filter((apt) => {
          const contact = apt.ghl_contacts;
          // Include appointments without contacts if searching
          if (!contact) return true;
          
          const fullName = `${contact.first_name || ''} ${contact.last_name || ''}`.toLowerCase();
          const email = (contact.email || '').toLowerCase();
          const company = (contact.company_name || '').toLowerCase();
          
          return (
            fullName.includes(searchLower) ||
            email.includes(searchLower) ||
            company.includes(searchLower)
          );
        });
      }

      setAppointments(filteredData);
    } catch (error: any) {
      console.error('Error fetching appointments:', error);
      toast({
        title: 'Error',
        description: 'Failed to load appointments',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const cancelAppointment = async (appointmentId: string, reason: string) => {
    try {
      const response = await supabase.functions.invoke('ghl-calendar', {
        body: { reason },
        method: 'DELETE',
      });

      if (response.error) throw response.error;

      toast({
        title: 'Success',
        description: 'Appointment cancelled successfully',
      });

      await fetchAppointments();
    } catch (error: any) {
      console.error('Error cancelling appointment:', error);
      toast({
        title: 'Error',
        description: 'Failed to cancel appointment',
        variant: 'destructive',
      });
    }
  };

  const markDealClosed = async (appointmentId: string) => {
    try {
      const { error } = await supabase
        .from('appointments')
        .update({
          deal_closed: true,
          deal_closed_at: new Date().toISOString(),
        })
        .eq('id', appointmentId);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Deal marked as closed',
      });

      await fetchAppointments();
    } catch (error: any) {
      console.error('Error marking deal as closed:', error);
      toast({
        title: 'Error',
        description: 'Failed to update appointment',
        variant: 'destructive',
      });
    }
  };

  const bulkMarkClosed = async (appointmentIds: string[]) => {
    try {
      const { error } = await supabase
        .from('appointments')
        .update({
          deal_closed: true,
          deal_closed_at: new Date().toISOString(),
        })
        .in('id', appointmentIds);

      if (error) throw error;

      // Log audit trail
      await supabase.rpc('log_admin_action', {
        p_action_type: 'bulk_close_appointments',
        p_action_details: {
          appointment_ids: appointmentIds,
          count: appointmentIds.length,
        },
      });

      toast({
        title: 'Success',
        description: `${appointmentIds.length} appointment(s) marked as closed`,
      });

      await fetchAppointments();
    } catch (error: any) {
      console.error('Error bulk closing appointments:', error);
      toast({
        title: 'Error',
        description: 'Failed to close appointments',
        variant: 'destructive',
      });
    }
  };

  const bulkCancel = async (appointmentIds: string[], reason: string) => {
    try {
      const { error } = await supabase
        .from('appointments')
        .update({
          status: 'cancelled',
          cancellation_reason: reason,
        })
        .in('id', appointmentIds);

      if (error) throw error;

      // Log audit trail
      await supabase.rpc('log_admin_action', {
        p_action_type: 'bulk_cancel_appointments',
        p_action_details: {
          appointment_ids: appointmentIds,
          count: appointmentIds.length,
          reason,
        },
      });

      toast({
        title: 'Success',
        description: `${appointmentIds.length} appointment(s) cancelled`,
      });

      await fetchAppointments();
    } catch (error: any) {
      console.error('Error bulk cancelling appointments:', error);
      toast({
        title: 'Error',
        description: 'Failed to cancel appointments',
        variant: 'destructive',
      });
    }
  };

  const reassignAppointment = async (
    appointmentId: string,
    newSalesRepId: string,
    justification: string
  ) => {
    try {
      const { error } = await supabase
        .from('appointments')
        .update({
          assigned_to_sales_rep_id: newSalesRepId,
        })
        .eq('id', appointmentId);

      if (error) throw error;

      // Log audit trail
      await supabase.rpc('log_admin_action', {
        p_action_type: 'reassign_appointment',
        p_action_details: {
          appointment_id: appointmentId,
          new_sales_rep_id: newSalesRepId,
          justification,
        },
      });

      toast({
        title: 'Success',
        description: 'Appointment reassigned successfully',
      });

      await fetchAppointments();
    } catch (error: any) {
      console.error('Error reassigning appointment:', error);
      toast({
        title: 'Error',
        description: 'Failed to reassign appointment',
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    fetchAppointments();

    const channel = supabase
      .channel('appointments_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'appointments',
        },
        () => {
          fetchAppointments();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [salesRepId, filters]);

  const archiveAppointment = async (appointmentId: string) => {
    try {
      const { error } = await supabase
        .from('appointments')
        .update({
          archived_at: new Date().toISOString(),
        })
        .eq('id', appointmentId);

      if (error) throw error;

      // Log audit trail
      await supabase.rpc('log_admin_action', {
        p_action_type: 'archive_appointment',
        p_action_details: {
          appointment_id: appointmentId,
        },
      });

      toast({
        title: 'Success',
        description: 'Appointment deleted successfully',
      });

      await fetchAppointments();
    } catch (error: any) {
      console.error('Error archiving appointment:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete appointment',
        variant: 'destructive',
      });
    }
  };

  const bulkArchive = async (appointmentIds: string[]) => {
    try {
      const { error } = await supabase
        .from('appointments')
        .update({
          archived_at: new Date().toISOString(),
        })
        .in('id', appointmentIds);

      if (error) throw error;

      // Log audit trail
      await supabase.rpc('log_admin_action', {
        p_action_type: 'bulk_archive_appointments',
        p_action_details: {
          appointment_ids: appointmentIds,
          count: appointmentIds.length,
        },
      });

      toast({
        title: 'Success',
        description: `${appointmentIds.length} appointment(s) deleted successfully`,
      });

      await fetchAppointments();
    } catch (error: any) {
      console.error('Error bulk archiving appointments:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete appointments',
        variant: 'destructive',
      });
    }
  };

  return {
    appointments,
    loading,
    fetchAppointments,
    cancelAppointment,
    markDealClosed,
    bulkMarkClosed,
    bulkCancel,
    reassignAppointment,
    archiveAppointment,
    bulkArchive,
  };
};